import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  List,
} from "@material-ui/core";
import { BulkSaveResult } from "pages/Accounts/accountContext";
import { useTranslation } from "react-i18next";
import { Alert } from "@material-ui/lab";
import AccountItem from "./AccountsView/AccountItem";
import { State } from "./reducer";
import { LoadedAccount } from "./types";

type Props = {
  state: State;
  back: () => void;
};

export default function SavedResultsView({ state, back }: Props) {
  const { t } = useTranslation("accounts");
  const { errors, accountsDone, succeeded } = state.saveResults;
  const isDelete = state.action === "DELETE";

  const resultRows = isDelete
    ? [
        ...succeeded.map(email => ({ Email: email, Result: "Deleted" })),
        ...errors.map(a => ({ Email: a.email, Result: a.error })),
      ].map(r => ({ ...r, Result: `"${r.Result.replace(/"/g, '""')}"` }))
    : errors.map(toUploadRow);

  return (
    <>
      <DialogContent>
        <Box pb={1}>
          {accountsDone === 0 && (
            <Alert variant="filled" severity="error">
              {t("bulk.allAccountsFailed")}
            </Alert>
          )}

          {accountsDone > 0 && (
            <Alert variant="filled" severity="success">
              {t(isDelete ? "bulk.deleteSuccess" : "bulk.success", {
                count: accountsDone,
              })}
            </Alert>
          )}

          {isDelete && succeeded.length > 0 && (
            <>
              <Box mt={4} fontWeight={500} fontSize="1rem">
                {t("bulk.deleted")}
              </Box>
              <List>
                {succeeded.map(email => (
                  <AccountItem
                    item={getLoadedAccount(state, email)}
                    key={email}
                  />
                ))}
              </List>
            </>
          )}

          {errors.length > 0 && (
            <Box mt={4} fontWeight={500} fontSize="1rem">
              {t("bulk.withErrors")}
            </Box>
          )}
          <List>
            {errors.map(accountWithError => {
              const loadedAccount = getLoadedAccount(
                state,
                accountWithError.email
              );

              return (
                <AccountItem
                  item={loadedAccount}
                  saveError={accountWithError.error}
                  key={loadedAccount.account.email}
                />
              );
            })}
          </List>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          variant="text"
          size="small"
          color="primary"
          component="a"
          download="results.csv"
          href={getResultFileHref(resultRows)}
        >
          {t("importAccountsModal.saveResults.downloadBtn")}
        </Button>
        <Button variant="outlined" size="small" onClick={back}>
          {t("importAccountsModal.goBackBtn")}
        </Button>
      </DialogActions>
    </>
  );
}

function getLoadedAccount(state: State, email: string): LoadedAccount {
  for (const file of state.loadedFiles) {
    const item = file.items.find(item => item.account.email === email);
    if (item) return item;
  }
}

// Email first, so the file can be uploaded again to retry (extra columns are ignored)
function getResultFileHref(formatedAccounts: Record<string, string>[]) {
  if (formatedAccounts.length === 0) return "";

  const header = Object.keys(formatedAccounts[0]);

  const rows = formatedAccounts.map(row => {
    return Object.values(row).join(",");
  });

  const content = [header.join(","), ...rows].join("\r\n");
  const file = new Blob([content], { type: "text/csv" });
  return URL.createObjectURL(file);
}

function toUploadRow(a: BulkSaveResult["errors"][number]) {
  return {
    Email: a.email,
    Aliases: (a.aliases || []).join(" "),
    "Box Account": a.storage?.box || "",
    "OneDrive Account": a.storage?.onedrive || "",
    "Google Drive Account": a.storage?.drive || "",
    "Dropbox Account": a.storage?.dropbox || "",
    "Tags (separated by spaces)": (a.tags || []).join(" "),
    "Approval groups (Id, group name or group email)": (a.approvalGroups || [])
      .map(groupId => `"${groupId}"`)
      .join(" "),
  };
}
